import * as Joi from "joi";
import parsePhoneNumber from "libphonenumber-js";
import { ROLES, SUBMERCHANT_PRICING_METHODS } from "src/constants";
import i18n from "src/i18n";

export const MERCHANT_BASIC_INFO_SCHEMA = Joi.object({
  managerName: Joi.string()
    .min(3)
    .required()
    .empty("")
    .label(i18n.t("Manager Name"))
    .pattern(/^(?=(?:.*[A-Za-zا-ي]){3,}).*$/)
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
      "string.empty": `{#label} ${i18n.t("validationErrors.isEmpty")}`,
       "string.pattern.base": `{#label} ${i18n.t("validationErrors.pattern")}`,
    }),
  companyName: Joi.string()
    .min(3)
    .required()
    .trim()
    .empty("")
    .pattern(/^(?=(?:.*[A-Za-zا-ي]){3,}).*$/)
    .label(i18n.t("Name"))
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
      "string.empty": `{#label} ${i18n.t("validationErrors.isEmpty")}`,
      "string.pattern.base": `{#label} ${i18n.t("validationErrors.pattern")}`,
      "string.min": `{#label} ${i18n.t("validationErrors.minChar", { length: 3 })}`,
    }),
  email: Joi.string()
    .email({ tlds: { allow: false } })
    .required()
    .empty("")
    .label(i18n.t("Email"))
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
      "string.empty": `{#label} ${i18n.t("validationErrors.isEmpty")}`,
      "email.invalid": `{#label} ${i18n.t("validationErrors.isEmail")}`,
    }),
  phone: Joi.string()
    .required()
    .label(i18n.t("Phone"))
    .empty("")
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
    })
    .custom((value, helper) => {
      const parsedPhone = parsePhoneNumber(value, { extract: true });
      if (!parsedPhone || !parsedPhone.isValid())
        return helper.message(i18n.t("validationErrors.isValidPhone"));
      return true;
    }),
});

export const MERCHANT_BUSINESS_INFO_SCHEMA = Joi.object({
  name: Joi.string()
    .required().empty("").trim()
    .pattern(/^(?=(?:.*[A-Za-zا-ي]){3,}).*$/)
    .label(i18n.t("Name"))
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
      "string.empty": `{#label} ${i18n.t("validationErrors.isEmpty")}`,
      "string.pattern.base": `{#label} ${i18n.t("validationErrors.pattern")}`,
    }),
  email: Joi.string()
    .email({ tlds: { allow: false } })
    .required()
    .empty("")
    .label(i18n.t("Email"))
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
      "string.empty": `{#label} ${i18n.t("validationErrors.isEmpty")}`,
      "email.invalid": `{#label} ${i18n.t("validationErrors.isEmail")}`,
    }),
  phone: Joi.string()
    .required()
    .label(i18n.t("Phone"))
    .empty("")
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
    })
    .custom((value, helper) => {
      const parsedPhone = parsePhoneNumber(value, { extract: true });
      if (!parsedPhone || !parsedPhone.isValid())
        return helper.message(i18n.t("validationErrors.isValidPhone"));
      return true;
    }),
  isCompany: Joi.boolean()
    .required()
    .label(i18n.t("Account Type"))
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
      "boolean.base": `{#label} ${i18n.t("validationErrors.isRequired")}`,
    }),

  commercialRegister: Joi.string().
    label(i18n.t("Commercial Register"))
    .when("isCompany", {
      is: true,
      otherwise: Joi.optional().strip(),
    }).messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
    }),

  commercialRegisterNumber: Joi.string()
    .label(i18n.t("Commercial Register Number"))
    .when("isCompany", {
      is: true,
      then: Joi.required().empty(""),
      otherwise: Joi.optional().strip(),
    }).messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
    }),

 taxNumber: Joi.string()
    .label(i18n.t("Tax Number"))
    .label(i18n.t("Tax Number")).pattern(/^3\d{13}3$/)
    .when("isCompany", {
      is: true,
      then: Joi.required().empty(""),
      otherwise: Joi.optional().strip(),
    }).messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
       "string.pattern.base": `{#label} ${i18n.t("validationErrors.taxNumberValidation")}`
    }),
    
  nationalAddress: Joi.string()
    .label(i18n.t("National Address"))
    .when("isCompany", {
      is: true,
      otherwise: Joi.optional().strip(),
    }),

  vatLicense: Joi.string()
    .label(i18n.t("VAT License"))
    .when("isCompany", {
      is: true,
      otherwise: Joi.optional().strip(),
    }),

  city: Joi.string()
    .label(i18n.t("City"))
    .when("isCompany", {
      is: true,
      then: Joi
        .required().empty(""),
      otherwise: Joi.optional().strip(),
    }).messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
    }),

  street: Joi.string()
    .label(i18n.t("Street"))
    .when("isCompany", {
      is: true,
      then: Joi.required().empty(""),
      otherwise: Joi.optional().strip(),
    }).messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
    }),

  buildingNumber: Joi.string()
    .label(i18n.t("Building Number"))
    .when("isCompany", {
      is: true,
      then: Joi.required().empty(""),
      otherwise: Joi.optional().strip(),
    }).messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
    }),

  district: Joi.string()
    .label(i18n.t("District"))
    .when("isCompany", {
      is: true,
      then: Joi.required().empty(""),
      otherwise: Joi.optional().strip(),
    }).messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
    }),

  zipCode: Joi.string()
  .label(i18n.t("Zip Code"))
  .when("isCompany", {
    is: true,
    then: Joi.optional().empty(""),
    otherwise: Joi.optional().strip(),
  }),
});

export const MERCHANT_BANK_ACCOUNT_INFO_SCHEMA = Joi.object({
  bank: Joi.string()
    .empty("")
    .required()
    .label(i18n.t("Bank"))
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
      "string.empty": `{#label} ${i18n.t("validationErrors.isEmpty")}`,
    }),
  accountName: Joi.string()
    .empty("")
    .required()
    .label(i18n.t("Account Holder Name"))
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
      "string.empty": `{#label} ${i18n.t("validationErrors.isEmpty")}`,
    }),
  iban: Joi.string()
    .empty("")
    .required()
    .label(i18n.t("IBAN"))
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
      "string.empty": `{#label} ${i18n.t("validationErrors.isEmpty")}`,
    }),
});

export const FREELANCE_DRIVER_BASIC_INFO_SCHEMA = Joi.object({
  name: Joi.string()
    .min(3)
    .required()
    .empty("")
    .label(i18n.t("Name"))
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
      "string.empty": `{#label} ${i18n.t("validationErrors.isEmpty")}`,
      "string.min": `{#label} ${i18n.t("validationErrors.minChar", { length: 3 })}`,
    }),
  email: Joi.string()
    .email({ tlds: { allow: false } })
    .required()
    .empty("")
    .label(i18n.t("Email"))
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
      "string.empty": `{#label} ${i18n.t("validationErrors.isEmpty")}`,
      "email.invalid": `{#label} ${i18n.t("validationErrors.isEmail")}`,
    }),
  phone: Joi.string()
    .required()
    .label(i18n.t("Phone"))
    .empty("")
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
    })
    .custom((value, helper) => {
      const parsedPhone = parsePhoneNumber(value, { extract: true });
      if (!parsedPhone || !parsedPhone.isValid())
        return helper.message(i18n.t("validationErrors.isValidPhone"));
      return true;
    }),
});

export const UPDATE_PASSWORD_SCHEMA = Joi.object({
  currentPassword: Joi.string()
    .required()
    .min(6)
    .label(i18n.t("Password"))
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
      "string.empty": `{#label} ${i18n.t("validationErrors.isEmpty")}`,
      "string.min": `{#label} ${i18n.t("validationErrors.minChar", { length: 6 })}`,
    }),
  password: Joi.string()
    .required()
    .empty("")
    .min(6)
    .label(i18n.t("Password"))
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
      "string.empty": `{#label} ${i18n.t("validationErrors.isEmpty")}`,
      "string.min": `{#label} ${i18n.t("validationErrors.minChar", { length: 6 })}`,
    }),
  confirm: Joi.string()
    .required()
    .empty("")
    .valid(Joi.ref("password"))
    .label(i18n.t("Confirm password"))
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
      "any.only": `{#label} ${i18n.t("validationErrors.isPasswordMatch")}`,
    }),
});

export const ADMIN_UPDATE_PASSWORD_SCHEMA = Joi.object({
  password: Joi.string()
    .required()
    .empty("")
    .min(6)
    .label(i18n.t("Password"))
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
      "string.empty": `{#label} ${i18n.t("validationErrors.isEmpty")}`,
      "string.min": `{#label} ${i18n.t("validationErrors.minChar", { length: 6 })}`,
    }),
  confirm: Joi.string()
    .required()
    .empty("")
    .valid(Joi.ref("password"))
    .label(i18n.t("Confirm password"))
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
      "any.only": `{#label} ${i18n.t("validationErrors.isPasswordMatch")}`,
    }),
});

export const LOCATION_PAGE_SETTINGS_SCHEMA = Joi.object({
  locationPageLogo: Joi.string()
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
    })
    .label(i18n.t("Logo")),
  locationPageColor: Joi.string()
    // .required()
    .allow(null, "")
    .label("Color"),
});

export const SALLA_INTEGRATION_SCHEMA = Joi.object({
  app_id: Joi.string()
    .required()
    .empty("")
    .label(i18n.t("App Id"))
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
    })
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
    }),
  client_id: Joi.string()
    .required()
    .empty("")
    .label(i18n.t("Client Id"))
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
    }),
  client_secret: Joi.string()
    .required()
    .empty("")
    .label(i18n.t("Client Secret Key"))
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
    }),
  // webhookSecretKey: Joi.string()
  //     .required()
  //     .label(i18n.t("Webhook Secret Key"))
  //     .messages({
  //         'any.required': `{#label} ${i18n.t("validationErrors.isRequired")}`,
  //     })
});

export const MERCHANT_STATUS_SCHEMA = Joi.object({
  status: Joi.string().valid("ACTIVE", "BLOCKED").required(),
});

export const ESTIMATION_TIME_SCHEMA = Joi.object({
  orderServiceTime: Joi.number()
    .min(1)
    .max(999999)
    .required()
    .label(i18n.t("Delivery time estimate"))
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
      "string.empty": `{#label} ${i18n.t("validationErrors.isEmpty")}`,
      "string.min": `{#label} ${i18n.t("validationErrors.minChar", { length: 1 })}`,
    }),
  arrivalTime: Joi.boolean(),
});

export const CONNECTION_SCHEMA = Joi.object({
  priceType: Joi.string()
    .valid(SUBMERCHANT_PRICING_METHODS.kg, SUBMERCHANT_PRICING_METHODS.piece)
    .required("required")
    .label(i18n.t("Pricing Method")),
  price: Joi.number()
    .required()
    .empty("")
    .default(1)
    .min(1)
    .label(i18n.t("Price"))
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
      "any.number": `{#label} ${i18n.t("validationErrors.isNumber")}`,
      "number.min": `{#label} ${i18n.t("validationErrors.minNumber", { length: 1 })}`,
    }),
  extraPiecePrice: Joi.number()
    .required()
    .empty("")
    .default(1)
    .min(1)
    .label(i18n.t("Extra piece price"))
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
      "number.min": `{#label} ${i18n.t("validationErrors.minNumber", { length: 1 })}`,
    }),
  COD_CollectFees: Joi.number()
    .required()
    .empty("")
    .default(1)
    .min(1)
    .label(i18n.t("COD"))
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
      "number.min": `{#label} ${i18n.t("validationErrors.minNumber", { length: 1 })}`,
    }),
  failedOrderPricingType: Joi.string()
    .required()
    .label(i18n.t("Failed order price type"))
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
    }),
  failedOrderPricingNumber: Joi.number()
    .label(i18n.t("Failed order price"))
    .when("failedOrderPricingType", {
      is: Joi.string().equal("SPECIFIC_NUMBER"),
      then: Joi.number().required().default(1).min(1),
      otherwise: Joi.number(),
    })
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
      "number.min": `{#label} ${i18n.t("validationErrors.minNumber", { length: 1 })}`,
    }),
  defaultKilograms: Joi.when("priceType", {
    is: SUBMERCHANT_PRICING_METHODS.kg,
    then: Joi.number()
      .required()
      .label(i18n.t("Base weight"))
      .empty("")
      .min(1)
      .default(1)
      .messages({
        "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
        "any.number": `{#label} ${i18n.t("validationErrors.isNumber")}`,
        "number.min": `{#label} ${i18n.t("validationErrors.minNumber", { length: 1 })}`,
      }),
    otherwise: Joi.number().label(i18n.t("Base weight")).empty("").optional(),
  }),
});

export const ESTIMATION_DAYS_FROM_TO_SCHEMA = Joi.object({
  from: Joi.number()
    .allow(null)
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
    })
    .label(i18n.t("From")),
  to: Joi.number()
    .allow(null)
    .when("from", {
      is: Joi.number().required(),
      then: Joi.number().greater(Joi.ref("from")).required(),
      otherwise: Joi.optional(),
    })
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
      "number.greater": `'{#label}' ${i18n.t("validationErrors.mustBeGreaterThan", { field: i18n.t("From") })}`,
    })
    .label(i18n.t("To")),
});

export const WALLET_TRANSACRION_SCHEMA = Joi.object({
  merchantId: Joi.string()
    .required()
    .empty("")
    .label(i18n.t("Name"))
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
      "string.empty": `{#label} ${i18n.t("validationErrors.isEmpty")}`,
    }),
  amount: Joi.number()
    .empty("")
    .required()
    .min(1)
    .label(i18n.t("Amount"))
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
      "number.min": `{#label} ${i18n.t("validationErrors.minNumber", { length: 1 })}`,
    }),
  description: Joi.string()
    .empty("")
    .required()
    .min(3)
    .label(i18n.t("Note"))
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
      "string.min": `{#label} ${i18n.t("validationErrors.minChar", { length: 3 })}`,
    }),
  type: Joi.string()
    .empty("")
    .valid("CREDIT", "DEBIT")
    .required()
    .label(i18n.t("Type"))
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
    }),
  shipmentId: Joi.string()
    .empty("").allow("")
    .optional()
    .label(i18n.t("Shipment Number"))
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
       "string.empty": `{#label} ${i18n.t("validationErrors.isEmpty")}`,
    }),
});

export const ADMIN_CREATE_USER_FOR_FREE_DEMO = Joi.object({
  logo: Joi.string()
    // .required()
    .label("Logo")
    .allow(null),
  companyName: Joi.string()
    .min(3)
    .required("required")
    .label(i18n.t("Company Name"))
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
      "string.empty": `{#label} ${i18n.t("validationErrors.isEmpty")}`,
      "string.min": `{#label} ${i18n.t("validationErrors.minChar", { length: 3 })}`,
    }),
  managerName: Joi.string()
    .min(3)
    .required("required")
    .label(i18n.t("Manager Name"))
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
      "string.empty": `{#label} ${i18n.t("validationErrors.isEmpty")}`,
      "string.min": `{#label} ${i18n.t("validationErrors.minChar", { length: 3 })}`,
    }),
  email: Joi.string()
    .email({ tlds: { allow: false } })
    .required("required")
    .label(i18n.t("Email"))
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
      "string.empty": `{#label} ${i18n.t("validationErrors.isEmpty")}`,
      "email.invalid": `{#label} ${i18n.t("validationErrors.isEmail")}`,
    }),
  phone: Joi.string()
    .required("required")
    .label(i18n.t("Phone"))
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
    })
    .custom((value, helper) => {
      const parsedPhone = parsePhoneNumber(value, { extract: true });
      if (!parsedPhone || !parsedPhone.isValid())
        return helper.message(i18n.t("validationErrors.isValidPhone"));
      return true;
    }),
  ordersRange: Joi.string()
    .required()
    .label(i18n.t("Monthly orders average"))
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
    }),
  role: Joi.string()
    .required()
    .label(i18n.t("Register as"))
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
    }),
  deliveryMethod: Joi.when("role", {
    is: ROLES.merchant,
    then: Joi.array()
      .required()
      .min(1)
      .label(i18n.t("Delivery method"))
      .messages({
        "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
        "array.min": `{#label} ${i18n.t("validationErrors.minArray", { number: 1 })}`,
        "any.only": `{#label} ${i18n.t("validationErrors.isDeliveryMethod")}`,
      }),
    otherwise: Joi.array()
      .optional()
      .allow(null)
      .label(i18n.t("Delivery method")),
  }),
});

export const SMS_PROVIDER_SCHEMA = Joi.object({
  provider: Joi.string()
    .valid("default", "unifonic", "taqnyat")
    .required()
    .empty("")
    .label(i18n.t("SMS Provider"))
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
      "string.empty": `{#label} ${i18n.t("validationErrors.isEmpty")}`,
      "any.only": `{#label} ${i18n.t("validationErrors.isInvalid")}`,
    }),
  domain: Joi.string()
    .optional()
    .empty("")
    .pattern(/^https?:\/\/[^\/]+\/$/)
    .label(i18n.t("Domain"))
    .messages({
      "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
      "string.empty": `{#label} ${i18n.t("validationErrors.isEmpty")}`,
      "string.pattern.base": `${i18n.t("validationErrors.isInvalidURLDomain")}`,
    }),
  settings: Joi.object().keys({
    apiKey: Joi.string()
      .required()
      .empty("")
      .min(3)
      .label(i18n.t("API Key"))
      .messages({
        "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
        "string.empty": `{#label} ${i18n.t("validationErrors.isEmpty")}`,
        "string.min": `{#label} ${i18n.t("validationErrors.minChar", { length: 3 })}`,
      }),
    senderId: Joi.string()
      .required()
      .empty("")
      .min(1)
      .label(i18n.t("Sender ID"))
      .messages({
        "any.required": `{#label} ${i18n.t("validationErrors.isRequired")}`,
        "string.empty": `{#label} ${i18n.t("validationErrors.isEmpty")}`,
        "string.min": `{#label} ${i18n.t("validationErrors.minChar", { length: 1 })}`,
      }),
  }),
});
